import React, { useEffect } from 'react';
import { FullPageError } from 'src/components/ErrorStates/FullPageError';

interface PageErrorStateProps {
  error?: unknown;
  reset?: () => void;
  resetErrorBoundary?: () => void;
}

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (error.name === 'ChunkLoadError') return true;
  if (
    error.message?.includes(
      "Cannot read properties of undefined (reading 'call')"
    )
  )
    return true;
  if (error.message?.includes('Loading CSS chunk')) return true;
  if (error.message?.includes('Failed to fetch dynamically imported module'))
    return true;
  return false;
}

const CHUNK_RELOAD_KEY = 'chunk-error-reload';
const CHUNK_RELOAD_COOLDOWN = 10_000;

const PageErrorState = ({
  error,
  reset,
  resetErrorBoundary,
}: PageErrorStateProps) => {
  const handleReset = resetErrorBoundary ?? reset;

  useEffect(() => {
    if (!error || !isChunkLoadError(error)) return;
    try {
      const last = sessionStorage.getItem(CHUNK_RELOAD_KEY);
      if (last && Date.now() - parseInt(last) < CHUNK_RELOAD_COOLDOWN) return;
      sessionStorage.setItem(CHUNK_RELOAD_KEY, Date.now().toString());
      window.location.reload();
    } catch {
      // sessionStorage unavailable — fall through to error UI
    }
  }, [error]);

  return (
    <FullPageError
      title="Oops! Something went wrong"
      description="We hit an unexpected issue. Please try again, and if the problem continues, contact your Customer Success team member for help."
      onButtonClick={handleReset}
      data-testid="error-boundary"
    />
  );
};

export default PageErrorState;
